// File: webhooks.ts
// Description: Webhooks page: lists, creates and edits incoming webhook endpoints and their action config.
// Author: Jose-Jorge HERNANDEZ
// Company: Parlee Conseiller, Inc.
// Date: 2026-06-23
// Last edit date: 2026-10-25
// Version: 1.1.0

import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { getUser } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';
import { CHANNELS, type Channel } from '../../shared/channels';

type WebhookActionType = 'send_message' | 'trigger_campaign' | 'trigger_template' | 'trigger_notification';

interface IncomingWebhook {
  id: string;
  slug: string;
  label: string;
  actionType: WebhookActionType;
  actionConfig: Record<string, string>;
  status: 'active' | 'disabled';
  createdAt: string;
}

interface CampaignOption {
  id: string;
  name: string;
}

interface TemplateOption {
  id: string;
  name: string;
  channel: Channel;
}

const ACTION_LABELS: Record<WebhookActionType, string> = {
  send_message: 'Send message',
  trigger_campaign: 'Trigger campaign',
  trigger_template: 'Trigger template',
  trigger_notification: 'Trigger notification',
};

const CHANNEL_LABELS: Record<Channel, string> = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email', webhook: 'Webhook' };

let webhooks: IncomingWebhook[] = [];

function canManage(): boolean {
  const role = getUser()?.role;
  return role === 'administrator' || role === 'manager';
}

function endpointUrl(slug: string): string {
  return `${location.origin}/api/webhooks/${slug}`;
}

async function loadWebhooks(container: HTMLElement): Promise<void> {
  webhooks = await apiFetch<IncomingWebhook[]>('/api/webhook-endpoints');
  renderList(container);
}

function showSecretOnce(secret: string, url: string): void {
  const { root, close } = openModal(
    'Save this secret now',
    `
    <p style="font-size:0.85rem;color:var(--clr-text-muted);margin-bottom:14px;">
      This is the only time the secret is shown. Callers must send it as the
      <code>X-Webhook-Secret</code> header or a <code>?secret=</code> query param.
    </p>
    <div class="field">
      <label>Endpoint URL</label>
      <input class="input" readonly value="${escapeHtml(url)}" id="secret-url">
    </div>
    <div class="field">
      <label>Secret</label>
      <input class="input" readonly value="${escapeHtml(secret)}" id="secret-value">
    </div>
    <div style="display:flex;justify-content:flex-end;">
      <button type="button" class="btn btn--primary" id="secret-close">Done</button>
    </div>
  `,
  );
  root.querySelector('#secret-close')?.addEventListener('click', close);
}

function renderList(container: HTMLElement): void {
  const listEl = container.querySelector('#webhooks-list')!;
  const manage = canManage();

  if (webhooks.length === 0) {
    listEl.innerHTML = `<p style="color:var(--clr-text-muted);">No webhook endpoints yet.</p>`;
    return;
  }

  listEl.innerHTML = webhooks
    .map(
      (hook) => `
    <div class="card" style="margin-bottom:12px;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div style="min-width:0;">
          <div style="font-weight:600;">${escapeHtml(hook.label)}</div>
          <div style="display:flex;gap:8px;margin-top:6px;flex-wrap:wrap;">
            <span class="badge badge--orange">${ACTION_LABELS[hook.actionType]}</span>
            <span class="badge ${hook.status === 'active' ? 'badge--success' : 'badge--muted'}">${hook.status}</span>
          </div>
          <div style="margin-top:8px;display:flex;align-items:center;gap:8px;">
            <code style="font-size:0.8rem;color:var(--clr-text-muted);">${escapeHtml(endpointUrl(hook.slug))}</code>
            <button class="btn btn--ghost" data-action="copy-url" data-slug="${hook.slug}" style="height:auto;padding:2px 6px;">Copy</button>
          </div>
        </div>
        ${
          manage
            ? `<div style="display:flex;gap:8px;flex-shrink:0;">
                 <button class="btn btn--ghost" data-action="edit" data-id="${hook.id}">Edit</button>
                 <button class="btn btn--ghost" data-action="regenerate" data-id="${hook.id}">Regenerate secret</button>
                 <button class="btn btn--ghost" data-action="delete" data-id="${hook.id}">Delete</button>
               </div>`
            : ''
        }
      </div>
    </div>`,
    )
    .join('');

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="copy-url"]').forEach((btn) => {
    btn.addEventListener('click', () => navigator.clipboard.writeText(endpointUrl(btn.dataset.slug!)));
  });

  if (!manage) return;

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const hook = webhooks.find((h) => h.id === btn.dataset.id);
      if (hook) openWebhookModal(container, hook);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="regenerate"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Regenerate the secret? The old one will stop working immediately.')) return;
      const hook = webhooks.find((h) => h.id === btn.dataset.id)!;
      const result = await apiFetch<{ secret: string }>(`/api/webhook-endpoints/${hook.id}/regenerate-secret`, { method: 'POST' });
      showSecretOnce(result.secret, endpointUrl(hook.slug));
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this webhook endpoint? Callers will start getting 404s immediately.')) return;
      await apiFetch(`/api/webhook-endpoints/${btn.dataset.id}`, { method: 'DELETE' });
      await loadWebhooks(container);
    });
  });
}

function openWebhookModal(container: HTMLElement, existing?: IncomingWebhook): void {
  const isEdit = Boolean(existing);
  const actionOptions = (Object.keys(ACTION_LABELS) as WebhookActionType[])
    .map((type) => `<option value="${type}" ${existing?.actionType === type ? 'selected' : ''}>${ACTION_LABELS[type]}</option>`)
    .join('');

  const { root, close } = openModal(
    isEdit ? 'Edit webhook endpoint' : 'New webhook endpoint',
    `
    <form id="webhook-form">
      <div class="field">
        <label for="webhook-label">Label</label>
        <input class="input" id="webhook-label" required value="${existing ? escapeHtml(existing.label) : ''}">
      </div>
      ${
        isEdit
          ? ''
          : `<div class="field">
               <label for="webhook-slug">Slug (optional)</label>
               <input class="input" id="webhook-slug" placeholder="auto-generated if left blank">
             </div>`
      }
      <div class="field">
        <label for="webhook-action-type">Action</label>
        <select class="input" id="webhook-action-type" ${isEdit ? 'disabled' : ''}>${actionOptions}</select>
      </div>
      <div id="webhook-config-fields"></div>
      <p class="error-text" id="webhook-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="webhook-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">${isEdit ? 'Save changes' : 'Create'}</button>
      </div>
    </form>
  `,
  );

  const actionSelect = root.querySelector<HTMLSelectElement>('#webhook-action-type')!;
  const fieldsContainer = root.querySelector<HTMLElement>('#webhook-config-fields')!;
  let campaigns: CampaignOption[] = [];
  let templates: TemplateOption[] = [];

  Promise.all([apiFetch<CampaignOption[]>('/api/campaigns'), apiFetch<TemplateOption[]>('/api/templates')])
    .then(([campaignList, templateList]) => {
      campaigns = campaignList;
      templates = templateList;
      // Only re-render for action types that need campaign/template select options.
      // send_message / trigger_notification don't use this data, so re-rendering would
      // wipe out any addresses the user has already added to the tag input.
      const currentType = actionSelect.value as WebhookActionType;
      if (currentType === 'trigger_campaign' || currentType === 'trigger_template') {
        renderConfigFields(currentType);
      }
    })
    .catch(() => undefined);

  function wireToTagInput(container: Element): void {
    const hidden = container.querySelector<HTMLInputElement>('#cfg-to');
    const inputEl = container.querySelector<HTMLInputElement>('#cfg-to-input');
    const addBtn = container.querySelector<HTMLButtonElement>('#cfg-to-add');
    const tagsEl = container.querySelector<HTMLElement>('#cfg-to-tags');
    if (!hidden || !inputEl || !addBtn || !tagsEl) return;

    const addresses: string[] = hidden.value ? hidden.value.split(',').map((s) => s.trim()).filter(Boolean) : [];

    function renderTags(): void {
      tagsEl!.innerHTML = addresses
        .map(
          (addr, i) =>
            `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:var(--color-surface-raised,#e8e8e8);border-radius:4px;font-size:0.85em;white-space:nowrap;">` +
            `${escapeHtml(addr)}` +
            `<button type="button" data-idx="${i}" style="background:none;border:none;cursor:pointer;padding:0 2px;line-height:1;font-size:1.1em;" title="Remove">&times;</button>` +
            `</span>`,
        )
        .join('');
      hidden!.value = addresses.join(',');
      tagsEl!.querySelectorAll<HTMLButtonElement>('button[data-idx]').forEach((btn) => {
        btn.addEventListener('click', () => {
          addresses.splice(parseInt(btn.dataset.idx!, 10), 1);
          renderTags();
        });
      });
    }

    function addCurrent(): void {
      const val = inputEl!.value.trim();
      if (val && !addresses.includes(val)) {
        addresses.push(val);
        renderTags();
        inputEl!.value = '';
      }
    }

    renderTags();
    addBtn.addEventListener('click', addCurrent);
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); addCurrent(); }
    });
  }

  function renderConfigFields(actionType: WebhookActionType): void {
    const config = existing?.actionConfig ?? {};

    if (actionType === 'send_message' || actionType === 'trigger_notification') {
      const channelOptions = CHANNELS.map(
        (ch) => `<option value="${ch}" ${config.channel === ch ? 'selected' : ''}>${CHANNEL_LABELS[ch]}</option>`,
      ).join('');
      const isWhatsApp = (config.channel ?? 'sms') === 'whatsapp';
      fieldsContainer.innerHTML = `
        <div class="field">
          <label for="cfg-channel">Channel</label>
          <select class="input" id="cfg-channel" data-key="channel">${channelOptions}</select>
        </div>
        <div class="field">
          <label>Recipient addresses (optional — falls back to the payload's "to" field)</label>
          <div id="cfg-to-tags" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:6px;min-height:0;"></div>
          <div style="display:flex;gap:8px;">
            <input class="input" id="cfg-to-input" placeholder="Add an address and press Enter" style="flex:1;">
            <button type="button" class="btn btn--secondary" id="cfg-to-add">Add</button>
          </div>
          <input type="hidden" id="cfg-to" data-key="to" value="${escapeHtml(config.to ?? '')}">
        </div>
        <div class="field">
          <label for="cfg-message">Message (supports {{variables}} from the incoming payload)</label>
          <textarea class="input" id="cfg-message" data-key="message" required>${escapeHtml(config.message ?? '')}</textarea>
        </div>
        <div id="cfg-content-sid-wrapper" class="field" style="${isWhatsApp ? '' : 'display:none;'}">
          <label for="cfg-contentSid">Content Template SID <span style="font-weight:400;color:var(--clr-text-muted);">(WhatsApp — HXxxx…)</span></label>
          <input class="input" id="cfg-contentSid" data-key="contentSid" placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value="${escapeHtml(config.contentSid ?? '')}">
          <p style="font-size:0.78rem;color:var(--clr-text-muted);margin-top:4px;">Required for business-initiated WhatsApp messages. In the message above, use the payload's field names (e.g. {{nombre}}), not {{1}}: they map to {{1}}, {{2}}, … in the Twilio template in order of appearance. An unmatched placeholder makes the trigger fail.</p>
        </div>
      `;
      wireToTagInput(fieldsContainer);
      const channelSel = fieldsContainer.querySelector<HTMLSelectElement>('#cfg-channel')!;
      const sidWrapper = fieldsContainer.querySelector<HTMLElement>('#cfg-content-sid-wrapper')!;
      const sidInput = fieldsContainer.querySelector<HTMLInputElement>('#cfg-contentSid')!;
      channelSel.addEventListener('change', () => {
        sidWrapper.style.display = channelSel.value === 'whatsapp' ? '' : 'none';
        if (channelSel.value !== 'whatsapp') sidInput.value = '';
      });
    } else if (actionType === 'trigger_campaign') {
      const options = campaigns.map((c) => `<option value="${c.id}" ${config.campaignId === c.id ? 'selected' : ''}>${escapeHtml(c.name)}</option>`).join('');
      fieldsContainer.innerHTML = `
        <div class="field">
          <label for="cfg-campaignId">Campaign to re-send</label>
          <select class="input" id="cfg-campaignId" data-key="campaignId"><option value="">— Select —</option>${options}</select>
        </div>
      `;
    } else {
      const options = templates
        .map((t) => `<option value="${t.id}" ${config.templateId === t.id ? 'selected' : ''}>${escapeHtml(t.name)} (${CHANNEL_LABELS[t.channel]})</option>`)
        .join('');
      fieldsContainer.innerHTML = `
        <div class="field">
          <label for="cfg-templateId">Template</label>
          <select class="input" id="cfg-templateId" data-key="templateId"><option value="">— Select —</option>${options}</select>
        </div>
        <div class="field">
          <label>Recipient addresses (optional — falls back to the payload's "to" field)</label>
          <div id="cfg-to-tags" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:6px;min-height:0;"></div>
          <div style="display:flex;gap:8px;">
            <input class="input" id="cfg-to-input" placeholder="Add an address and press Enter" style="flex:1;">
            <button type="button" class="btn btn--secondary" id="cfg-to-add">Add</button>
          </div>
          <input type="hidden" id="cfg-to" data-key="to" value="${escapeHtml(config.to ?? '')}">
        </div>
      `;
      wireToTagInput(fieldsContainer);
    }
  }

  renderConfigFields(actionSelect.value as WebhookActionType);
  actionSelect.addEventListener('change', () => renderConfigFields(actionSelect.value as WebhookActionType));

  root.querySelector('#webhook-cancel')?.addEventListener('click', close);

  root.querySelector('#webhook-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#webhook-error')!;
    errorEl.textContent = '';

    const label = (root.querySelector('#webhook-label') as HTMLInputElement).value.trim();
    const config: Record<string, string> = {};
    fieldsContainer.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-key]').forEach((field) => {
      if (field.value.trim()) config[field.dataset.key!] = field.value.trim();
    });

    try {
      if (isEdit && existing) {
        await apiFetch(`/api/webhook-endpoints/${existing.id}`, { method: 'PATCH', body: JSON.stringify({ label, config }) });
        close();
      } else {
        const slug = (root.querySelector('#webhook-slug') as HTMLInputElement).value.trim();
        const actionType = actionSelect.value as WebhookActionType;
        const created = await apiFetch<IncomingWebhook & { secret: string }>('/api/webhook-endpoints', {
          method: 'POST',
          body: JSON.stringify({ label, slug: slug || undefined, actionType, config }),
        });
        close();
        showSecretOnce(created.secret, endpointUrl(created.slug));
      }
      await loadWebhooks(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/webhooks',
  title: 'Webhooks',
  render: ({ container }) => {
    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Webhooks</h1>
          <p>Generate endpoints external systems can call to trigger a send, a campaign, or a template.</p>
        </div>
        ${canManage() ? '<button class="btn btn--primary" id="new-webhook-btn">+ New webhook</button>' : ''}
      </div>
      <div id="webhooks-list">Loading…</div>
    `;

    container.querySelector('#new-webhook-btn')?.addEventListener('click', () => openWebhookModal(container));

    loadWebhooks(container).catch(() => {
      container.querySelector('#webhooks-list')!.textContent = 'Could not load webhook endpoints.';
    });
  },
});
